import dayjs from 'dayjs';
import { ToWords } from 'to-words';

const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';

/**
 * Generate the DSR Summary document
 *
 * @param {Object=} data - The data to be used to generate the document
 */
export const generateDSRSummary = (data = {}) => {
  const borderColor = '#9ca3af';
  const bigMargin = 20;
  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Debt Service Ratio Summary', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'dsr summary, loan, finance', // keywords associated with the document
      author: data.company?.name, // company name
      subject: 'Debt Service Ratio Summary', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        alignment: 'center',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        alignment: 'center',
        fontSize: 22,
        bold: true
      },
      {
        text: 'Statement of Monthly Income & Expenses',
        alignment: 'center',
        fontSize: 14
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: bigMargin,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          body: [
            [`Name: John Doe`, `Account #: ${data.loan?.mainAccountNumber}`],
            [`Loan ID: N/A`, `Member Category: Member`],
            [
              `Loan Type: Motor Vehicle`,
              `Monthly Payment: ${formatToCurrency(43750)}`
            ]
          ]
        }
      },
      {
        text: 'Gross Income',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*', '*', '*'],
          headerRows: 1,

          body: [
            [
              { text: 'Source of Income', style: 'tableHeader' },
              { text: 'Description', style: 'tableHeader' },
              { text: 'Type', style: 'tableHeader' },
              { text: 'Amount', style: 'tableHeader' }
            ],
            ['Salary', 'job', 'income', formatToCurrency(795000)],
            [
              'Business Income',
              'car rental',
              'income',
              formatToCurrency(195000)
            ]
          ]
        }
      },
      {
        text: 'Less Deductions At Source',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Deductions', style: 'tableHeader' },
              { text: 'Amount', style: 'tableHeader' }
            ],
            ['Pension', formatToCurrency(6000)],
            ['Total Tax Deduction ', formatToCurrency(289356.2)]
          ]
        }
      },
      {
        text: 'Net Income',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Total', style: 'tableHeader' },
              { text: formatToCurrency(694643.8), style: 'tableHeader' }
            ]
          ]
        }
      },
      {
        text: 'Debts',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Less Current Debt Payments', style: 'tableHeader' },
              { text: 'Amount', style: 'tableHeader' }
            ],
            ['Loan Payment', formatToCurrency(503.52)],
            ['Credit Card', formatToCurrency(5000000)],
            ['Loan Payment', formatToCurrency(43750.0)]
          ]
        }
      },
      {
        text: 'Expenses',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Less Mandatory Expenses', style: 'tableHeader' },
              { text: 'Amount', style: 'tableHeader' }
            ],
            ['Utilities', formatToCurrency(5032.52)],
            ['Rent', formatToCurrency(75000)],
            ['Groceries', formatToCurrency(43750.0)]
          ]
        }
      },
      {
        text: 'Total Debts & Expenses',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Total', style: 'tableHeader' },
              { text: formatToCurrency(694643.8), style: 'tableHeader' }
            ]
          ]
        }
      },
      {
        text: 'DSR Calculations',
        style: 'tableHeading'
      },
      {
        alignment: 'left',
        layout: tableLayout,
        marginTop: 10,
        marginBottom: bigMargin,
        table: {
          widths: ['*', '*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'Ratio Name', style: 'tableHeader' },
              { text: 'Rating', style: 'tableHeader' },
              { text: 'Ratio', style: 'tableHeader' }
            ],
            ['Income to Expense Ratio', 'low', '44.81%'],
            ['DSR', 'low', '9.52%']
          ]
        }
      },
      {
        pageBreak: 'before',
        text: 'Declaration of Member',
        style: 'tableHeading'
      },
      {
        text: 'I hereby confirm that the information given herein is true in all respects, accurate and complete and that I have not withheld any information that might affect the Credit Union’s decision. I am agreeable to the Credit Union taking such steps, as it may deem necessary to verify any of the information given.'
      },
      {
        marginTop: bigMargin * 2,
        columns: [
          {
            text: 'Thomas Campbell',
            bold: true,
            alignment: 'center'
          },
          {
            text: '__________________________',
            font: 'Roboto',
            alignment: 'center'
          },
          {
            text: dayjs().format('YYYY-MM-DD'),
            alignment: 'center',

            bold: true
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: 'Name of Borrower',
            style: 'supSignature'
          },
          {
            text: 'Signature of Borrower',
            style: 'supSignature'
          },
          {
            text: 'Date',
            style: 'supSignature'
          }
        ]
      },
      {
        marginTop: bigMargin * 2,
        columns: [
          {
            text: '__________________________',
            font: 'Roboto',
            alignment: 'center'
          },
          {
            text: '__________________________',
            font: 'Roboto',
            alignment: 'center'
          },
          {
            text: dayjs().format('YYYY-MM-DD'),
            alignment: 'center',
            bold: true
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: 'Name of Witness',
            style: 'supSignature'
          },
          {
            text: 'Signature of Witness',
            style: 'supSignature'
          },
          {
            text: 'Date',
            style: 'supSignature'
          }
        ]
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
